import React, { useState, useRef, useCallback } from 'react';
import { Modal, ProgressBar } from 'react-bootstrap';
import { FaFileUpload, FaDownload, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaTimes, FaFileExcel, FaFileCsv } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  CampoImportacao,
  ImportResult,
  LogEntry,
  PlanoImportacao,
  ItemPlano,
  lerArquivo,
  processarImportacao,
  montarPlanoImportacao,
  processarPlano,
  gerarTemplate,
} from '../../services/importacao';

interface ModalImportacaoProps {
  show: boolean;
  onHide: () => void;
  titulo: string;
  campos: CampoImportacao[];
  nomeTemplate: string;
  camposFixos: Record<string, any>;
  enviar: (item: Record<string, any>) => Promise<void>;
  onImportacaoConcluida?: () => void;
  /** Habilita detecção de divergência + aprovação para registros já existentes (upsert). */
  permitirAtualizacao?: boolean;
  /** Campo do DTO usado para casar com registros existentes (ex: 'documento', 'placa'). */
  chaveMatch?: string;
  /** Campo usado como rótulo amigável nos itens (default 'nome'). */
  rotuloCampo?: string;
  /** Busca todos os registros existentes (p/ comparar). Necessário p/ permitirAtualizacao. */
  buscarExistentes?: () => Promise<Record<string, any>[]>;
  /** Atualiza um registro existente (PUT). Necessário p/ permitirAtualizacao. */
  atualizar?: (item: Record<string, any>) => Promise<void>;
}

type Etapa = 'upload' | 'analisando' | 'revisao' | 'importando' | 'resultado';

const ModalImportacao: React.FC<ModalImportacaoProps> = ({
  show,
  onHide,
  titulo,
  campos,
  nomeTemplate,
  camposFixos,
  enviar,
  onImportacaoConcluida,
  permitirAtualizacao,
  chaveMatch,
  rotuloCampo,
  buscarExistentes,
  atualizar,
}) => {
  const [etapa, setEtapa] = useState<Etapa>('upload');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [progresso, setProgresso] = useState(0);
  const [progressoTotal, setProgressoTotal] = useState(0);
  const [resultado, setResultado] = useState<ImportResult | null>(null);
  const [plano, setPlano] = useState<PlanoImportacao | null>(null);
  const [filtroLog, setFiltroLog] = useState<'todos' | 'sucesso' | 'erro' | 'aviso'>('todos');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const upsertHabilitado = !!(permitirAtualizacao && chaveMatch && buscarExistentes && atualizar);

  const resetar = useCallback(() => {
    setEtapa('upload');
    setArquivo(null);
    setProgresso(0);
    setProgressoTotal(0);
    setResultado(null);
    setPlano(null);
    setFiltroLog('todos');
    setIsDragging(false);
  }, []);

  const handleClose = () => {
    if (etapa === 'importando' || etapa === 'analisando') {
      toast.warning('Aguarde a operação finalizar.');
      return;
    }
    resetar();
    onHide();
  };

  const aceitarArquivo = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext || '')) {
      toast.error('Formato inválido. Use .xlsx, .xls ou .csv');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Tamanho máximo: 10MB.');
      return;
    }
    setArquivo(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) aceitarArquivo(file);
    if (e.target) e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) aceitarArquivo(file);
  };

  const handleIniciarImportacao = async () => {
    if (!arquivo) return;

    // Fluxo com upsert: lê + busca existentes + monta plano (diff) e vai pra revisão.
    if (upsertHabilitado) {
      setEtapa('analisando');
      try {
        const dados = await lerArquivo(arquivo);
        const existentes = await buscarExistentes!();
        const p = montarPlanoImportacao(dados, campos, camposFixos, existentes, chaveMatch!, rotuloCampo || 'nome');

        if (p.itens.length === 0) {
          const e = p.logsValidacao.find(l => l.tipo === 'erro');
          toast.error(e?.mensagem || 'Nada para importar no arquivo.');
          setEtapa('upload');
          return;
        }

        setPlano(p);
        setEtapa('revisao');
      } catch (err: any) {
        toast.error(err.message || 'Erro ao analisar o arquivo.');
        setEtapa('upload');
      }
      return;
    }

    // Fluxo legado (create-only).
    setEtapa('importando');
    setProgresso(0);

    try {
      const dados = await lerArquivo(arquivo);
      setProgressoTotal(Math.max(dados.length - 1, 1));

      const result = await processarImportacao(
        dados,
        campos,
        camposFixos,
        enviar,
        (atual, total) => {
          setProgresso(atual);
          setProgressoTotal(total);
        }
      );

      setResultado(result);
      setEtapa('resultado');

      if (result.sucesso > 0) {
        onImportacaoConcluida?.();
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao processar arquivo.');
      setEtapa('upload');
    }
  };

  const handleConfirmarPlano = async () => {
    if (!plano) return;
    setEtapa('importando');
    setProgresso(0);
    try {
      const result = await processarPlano(
        plano,
        enviar,
        atualizar!,
        (atual, total) => { setProgresso(atual); setProgressoTotal(total); }
      );
      setResultado(result);
      setEtapa('resultado');
      if (result.sucesso > 0) onImportacaoConcluida?.();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao aplicar a importação.');
      setEtapa('revisao');
    }
  };

  // Alterna aprovação de um item divergente (por linha).
  const toggleAprovacao = (linha: number) => {
    setPlano(prev => prev ? {
      ...prev,
      itens: prev.itens.map(it => it.linha === linha ? { ...it, aprovado: !it.aprovado } : it),
    } : prev);
  };

  const setAprovacaoDivergentes = (aprovado: boolean) => {
    setPlano(prev => prev ? {
      ...prev,
      itens: prev.itens.map(it => it.tipo === 'divergente' ? { ...it, aprovado } : it),
    } : prev);
  };

  const divergentes = plano?.itens.filter(it => it.tipo === 'divergente') || [];
  const aprovadosCount = plano?.itens.filter(it => (it.tipo === 'novo' || it.tipo === 'divergente') && it.aprovado).length || 0;

  const logsFiltrados = resultado?.logs.filter(log => {
    if (filtroLog === 'todos') return true;
    return log.tipo === filtroLog;
  }) || [];

  const percentual = progressoTotal > 0 ? Math.round((progresso / progressoTotal) * 100) : 0;

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
      <Modal.Header style={{ backgroundColor: '#1F467E', color: 'white', padding: '12px 16px' }}>
        <Modal.Title style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FaFileUpload /> {titulo}
        </Modal.Title>
        <button onClick={handleClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '18px' }}>
          <FaTimes />
        </button>
      </Modal.Header>

      <Modal.Body style={{ padding: '20px', minHeight: '400px' }}>
        {/* ─── ETAPA: UPLOAD ─── */}
        {etapa === 'upload' && (
          <div>
            {/* Instruções */}
            <div style={{ background: '#f0f4ff', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #d0d9f0' }}>
              <h6 style={{ margin: '0 0 8px', fontWeight: 600, color: '#1F467E', fontSize: '14px' }}>Como importar:</h6>
              <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#444', lineHeight: '1.8' }}>
                <li>Baixe o <strong>template</strong> clicando no botão abaixo</li>
                <li>Preencha os dados no arquivo (campos com <strong>*</strong> são obrigatórios)</li>
                <li>Salve e faça upload do arquivo preenchido</li>
              </ol>
            </div>

            {/* Botão template */}
            <button
              onClick={() => gerarTemplate(campos, nomeTemplate)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 16px', borderRadius: '6px',
                border: '2px dashed #1F467E', background: 'white',
                color: '#1F467E', fontWeight: 600, cursor: 'pointer',
                width: '100%', justifyContent: 'center', marginBottom: '16px',
                fontSize: '14px', transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = '#f0f4ff'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'white'; }}
            >
              <FaDownload /> Baixar Template de Importação
            </button>

            {/* Área de upload */}
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${isDragging ? '#1F467E' : '#ccc'}`,
                borderRadius: '8px',
                padding: '40px 20px',
                textAlign: 'center',
                cursor: 'pointer',
                background: isDragging ? '#f0f4ff' : '#fafafa',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '12px' }}>
                <FaFileExcel style={{ fontSize: '32px', color: '#217346' }} />
                <FaFileCsv style={{ fontSize: '32px', color: '#f47b20' }} />
              </div>
              <p style={{ margin: '0 0 4px', fontWeight: 600, color: '#333', fontSize: '14px' }}>
                {isDragging ? 'Solte o arquivo aqui' : 'Arraste ou clique para selecionar'}
              </p>
              <p style={{ margin: 0, color: '#888', fontSize: '12px' }}>
                Formatos aceitos: .xlsx, .xls, .csv (máx. 10MB)
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {/* Arquivo selecionado */}
            {arquivo && (
              <div style={{
                marginTop: '16px', padding: '12px 16px', borderRadius: '6px',
                background: '#e8f5e9', border: '1px solid #c8e6c9',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaCheckCircle style={{ color: '#2e7d32' }} />
                  <span style={{ fontWeight: 500, fontSize: '13px' }}>{arquivo.name}</span>
                  <span style={{ color: '#666', fontSize: '12px' }}>({(arquivo.size / 1024).toFixed(1)} KB)</span>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setArquivo(null); }}
                  style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer' }}
                >
                  <FaTimes />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ─── ETAPA: ANALISANDO ─── */}
        {etapa === 'analisando' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '20px' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '48px', height: '48px' }}>
              <span className="visually-hidden">Analisando...</span>
            </div>
            <h5 style={{ margin: 0, color: '#333' }}>Analisando arquivo...</h5>
            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
              Comparando com os registros já cadastrados
            </p>
          </div>
        )}

        {/* ─── ETAPA: IMPORTANDO ─── */}
        {etapa === 'importando' && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '20px' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '48px', height: '48px' }}>
              <span className="visually-hidden">Importando...</span>
            </div>
            <h5 style={{ margin: 0, color: '#333' }}>Importando dados...</h5>
            <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
              {progresso} de {progressoTotal} registros processados
            </p>
            <div style={{ width: '100%', maxWidth: '400px' }}>
              <ProgressBar
                now={percentual}
                label={`${percentual}%`}
                animated
                striped
                variant="primary"
                style={{ height: '24px' }}
              />
            </div>
            <p style={{ margin: 0, color: '#999', fontSize: '12px' }}>
              Não feche esta janela durante a importação
            </p>
          </div>
        )}

        {/* ─── ETAPA: REVISÃO (upsert) ─── */}
        {etapa === 'revisao' && plano && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
              <SummaryCard label="Novos" value={plano.novos} color="#2e7d32" icon={<FaCheckCircle />} />
              <SummaryCard label="A atualizar" value={plano.divergentes} color="#1565c0" icon={<FaExclamationTriangle />} />
              <SummaryCard label="Sem mudança" value={plano.iguais} color="#777" icon={<FaCheckCircle />} />
              <SummaryCard label="Inválidos" value={plano.invalidos} color="#c62828" icon={<FaTimesCircle />} />
            </div>

            <div style={{ background: '#f0f4ff', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: '#444' }}>
              Serão <strong>criados {plano.novos}</strong> registro(s) novo(s). Os <strong>{plano.divergentes}</strong> já
              existentes com diferenças aparecem abaixo — marque os que deseja atualizar. Colunas vazias na planilha
              <strong> não apagam</strong> dados existentes.
            </div>

            {divergentes.length > 0 ? (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <strong style={{ fontSize: '14px', color: '#333' }}>Alterações a aprovar ({divergentes.length})</strong>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setAprovacaoDivergentes(true)} style={{ background: 'none', border: 'none', color: '#1565c0', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Marcar todos</button>
                    <button onClick={() => setAprovacaoDivergentes(false)} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>Desmarcar todos</button>
                  </div>
                </div>
                <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                  {divergentes.map(it => (
                    <DivergenciaRow key={it.linha} item={it} onToggle={() => toggleAprovacao(it.linha)} />
                  ))}
                </div>
              </>
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#777', fontSize: '13px' }}>
                Nenhum registro existente com diferenças. {plano.novos > 0 ? 'Apenas novos serão criados.' : ''}
              </div>
            )}

            {/* Motivo das linhas inválidas — não serão importadas */}
            {plano.invalidos > 0 && (
              <div style={{ marginTop: '16px' }}>
                <strong style={{ fontSize: '14px', color: '#c62828' }}>
                  Linhas inválidas ({plano.invalidos}) — não serão importadas
                </strong>
                <div style={{ maxHeight: '160px', overflowY: 'auto', border: '1px solid #ffcdd2', borderRadius: '6px', marginTop: '8px' }}>
                  {plano.logsValidacao.filter(l => l.tipo === 'erro').map((log, idx) => (
                    <LogRow key={idx} log={log} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── ETAPA: RESULTADO ─── */}
        {etapa === 'resultado' && resultado && (
          <div>
            {/* Cards de resumo */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '16px' }}>
              <SummaryCard label="Total" value={resultado.total} color="#1F467E" icon={<FaFileUpload />} />
              <SummaryCard label="Sucesso" value={resultado.sucesso} color="#2e7d32" icon={<FaCheckCircle />} />
              <SummaryCard label="Erros" value={resultado.erros} color="#c62828" icon={<FaTimesCircle />} />
              <SummaryCard label="Avisos" value={resultado.avisos} color="#f57f17" icon={<FaExclamationTriangle />} />
            </div>

            {/* Barra de progresso visual */}
            {resultado.total > 0 && (
              <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', height: '8px', marginBottom: '16px' }}>
                {resultado.sucesso > 0 && <div style={{ width: `${(resultado.sucesso / resultado.total) * 100}%`, background: '#2e7d32' }} />}
                {resultado.erros > 0 && <div style={{ width: `${(resultado.erros / resultado.total) * 100}%`, background: '#c62828' }} />}
                {(resultado.total - resultado.sucesso - resultado.erros) > 0 && <div style={{ width: `${((resultado.total - resultado.sucesso - resultado.erros) / resultado.total) * 100}%`, background: '#e0e0e0' }} />}
              </div>
            )}

            {/* Filtros de log */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              <FilterChip label="Todos" count={resultado.logs.length} active={filtroLog === 'todos'} onClick={() => setFiltroLog('todos')} color="#555" />
              <FilterChip label="Sucesso" count={resultado.logs.filter(l => l.tipo === 'sucesso').length} active={filtroLog === 'sucesso'} onClick={() => setFiltroLog('sucesso')} color="#2e7d32" />
              <FilterChip label="Erros" count={resultado.logs.filter(l => l.tipo === 'erro').length} active={filtroLog === 'erro'} onClick={() => setFiltroLog('erro')} color="#c62828" />
              <FilterChip label="Avisos" count={resultado.logs.filter(l => l.tipo === 'aviso').length} active={filtroLog === 'aviso'} onClick={() => setFiltroLog('aviso')} color="#f57f17" />
            </div>

            {/* Lista de logs */}
            <div style={{
              maxHeight: '250px', overflowY: 'auto',
              border: '1px solid #e0e0e0', borderRadius: '6px',
              fontSize: '13px',
            }}>
              {logsFiltrados.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  Nenhum registro encontrado para este filtro.
                </div>
              ) : (
                logsFiltrados.map((log, idx) => (
                  <LogRow key={idx} log={log} />
                ))
              )}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ padding: '12px 16px', borderTop: '1px solid #e0e0e0' }}>
        {etapa === 'upload' && (
          <>
            <button
              onClick={handleClose}
              style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #ccc', background: 'white', color: '#555', cursor: 'pointer', fontSize: '14px' }}
            >
              Cancelar
            </button>
            <button
              onClick={handleIniciarImportacao}
              disabled={!arquivo}
              style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none',
                background: arquivo ? '#1F467E' : '#ccc',
                color: 'white', cursor: arquivo ? 'pointer' : 'not-allowed',
                fontWeight: 600, fontSize: '14px',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}
            >
              <FaFileUpload /> Iniciar Importação
            </button>
          </>
        )}
        {etapa === 'revisao' && (
          <>
            <button
              onClick={() => setEtapa('upload')}
              style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #ccc', background: 'white', color: '#555', cursor: 'pointer', fontSize: '14px' }}
            >
              Voltar
            </button>
            <button
              onClick={handleConfirmarPlano}
              disabled={aprovadosCount === 0}
              style={{
                padding: '8px 20px', borderRadius: '6px', border: 'none',
                background: aprovadosCount > 0 ? '#1F467E' : '#ccc',
                color: 'white', cursor: aprovadosCount > 0 ? 'pointer' : 'not-allowed',
                fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px',
              }}
            >
              <FaCheckCircle /> Confirmar ({aprovadosCount})
            </button>
          </>
        )}
        {etapa === 'resultado' && (
          <>
            <button
              onClick={() => { resetar(); }}
              style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #1F467E', background: 'white', color: '#1F467E', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
            >
              Nova Importação
            </button>
            <button
              onClick={handleClose}
              style={{ padding: '8px 20px', borderRadius: '6px', border: 'none', background: '#1F467E', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '14px' }}
            >
              Fechar
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// ─── Subcomponentes ───

const SummaryCard: React.FC<{ label: string; value: number; color: string; icon: React.ReactNode }> = ({ label, value, color, icon }) => (
  <div style={{
    textAlign: 'center', padding: '12px 8px', borderRadius: '8px',
    background: `${color}10`, border: `1px solid ${color}30`,
  }}>
    <div style={{ color, fontSize: '20px', marginBottom: '4px' }}>{icon}</div>
    <div style={{ fontSize: '24px', fontWeight: 700, color }}>{value}</div>
    <div style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>{label}</div>
  </div>
);

const FilterChip: React.FC<{ label: string; count: number; active: boolean; onClick: () => void; color: string }> = ({ label, count, active, onClick, color }) => (
  <button
    onClick={onClick}
    style={{
      padding: '4px 12px', borderRadius: '20px', fontSize: '12px',
      border: `1px solid ${active ? color : '#ccc'}`,
      background: active ? `${color}15` : 'white',
      color: active ? color : '#666',
      cursor: 'pointer', fontWeight: active ? 600 : 400,
      display: 'flex', alignItems: 'center', gap: '4px',
    }}
  >
    {label} <span style={{ background: active ? color : '#e0e0e0', color: active ? 'white' : '#666', borderRadius: '10px', padding: '0 6px', fontSize: '11px' }}>{count}</span>
  </button>
);

const LogRow: React.FC<{ log: LogEntry }> = ({ log }) => {
  const config = {
    sucesso: { icon: <FaCheckCircle />, color: '#2e7d32', bg: '#e8f5e9' },
    erro: { icon: <FaTimesCircle />, color: '#c62828', bg: '#ffebee' },
    aviso: { icon: <FaExclamationTriangle />, color: '#f57f17', bg: '#fff8e1' },
  }[log.tipo];

  return (
    <div style={{
      padding: '8px 12px', borderBottom: '1px solid #f0f0f0',
      display: 'flex', alignItems: 'flex-start', gap: '8px',
      background: config.bg,
    }}>
      <span style={{ color: config.color, marginTop: '2px', flexShrink: 0 }}>{config.icon}</span>
      <div style={{ flex: 1 }}>
        <span style={{ color: '#333' }}>{log.mensagem}</span>
      </div>
      {log.linha > 0 && (
        <span style={{ color: '#999', fontSize: '11px', whiteSpace: 'nowrap', flexShrink: 0 }}>
          Linha {log.linha}
        </span>
      )}
    </div>
  );
};

const formatarValor = (v: any): string => {
  if (v === null || v === undefined || v === '') return '(vazio)';
  const s = String(v);
  // datas ISO → DD/MM/AAAA
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return s;
};

const DivergenciaRow: React.FC<{ item: ItemPlano; onToggle: () => void }> = ({ item, onToggle }) => (
  <div style={{
    padding: '10px 12px', borderBottom: '1px solid #f0f0f0',
    background: item.aprovado ? '#f3f8ff' : '#fafafa',
    display: 'flex', gap: '10px', alignItems: 'flex-start',
  }}>
    <input
      type="checkbox"
      checked={item.aprovado}
      onChange={onToggle}
      style={{ marginTop: '3px', cursor: 'pointer', width: '16px', height: '16px', flexShrink: 0 }}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
        <strong style={{ fontSize: '13px', color: '#333' }}>{item.rotulo}</strong>
        <span style={{ color: '#999', fontSize: '11px', whiteSpace: 'nowrap', flexShrink: 0 }}>Linha {item.linha}</span>
      </div>
      <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {item.diffs?.map((d, i) => (
          <div key={i} style={{ fontSize: '12px', color: '#555' }}>
            <span style={{ fontWeight: 600 }}>{d.header.replace(/\*/g, '')}:</span>{' '}
            <span style={{ color: '#c62828', textDecoration: 'line-through' }}>{formatarValor(d.de)}</span>
            {' → '}
            <span style={{ color: '#2e7d32', fontWeight: 600 }}>{formatarValor(d.para)}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export default ModalImportacao;
